import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Pagination } from "@/components/Pagination";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Users, Edit, Mail, Loader2, Search, Eye, EyeOff, Scissors, Plus, Trash2, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

export default function CustomersPage() {
  const { isSuperAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all | active | inactive
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const perPage = 50;

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronsUpDown size={12} className="ml-1 opacity-40 inline-block" />;
    return sortDir === "asc" ? <ChevronUp size={12} className="ml-1 inline-block" /> : <ChevronDown size={12} className="ml-1 inline-block" />;
  };

  const sortedCustomers = useMemo(() => {
    let list = [...customers];
    if (sortField) {
      list.sort((a, b) => {
        let av = a[sortField] ?? "", bv = b[sortField] ?? "";
        if (typeof av === "boolean") return sortDir === "asc" ? (av === bv ? 0 : av ? -1 : 1) : (av === bv ? 0 : av ? 1 : -1);
        return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return list;
  }, [customers, sortField, sortDir]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState({});
  const [togglingActive, setTogglingActive] = useState({});
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activatingCustomer, setActivatingCustomer] = useState(null);
  const [activatePassword, setActivatePassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    full_name: "", company_name: "", company_address: "",
    gst_number: "", contact_phone: "", cutting_cost_matrix: []
  });

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      if (search) params.append("search", search);
      if (activeFilter === "active") params.append("is_active", "true");
      if (activeFilter === "inactive") params.append("is_active", "false");
      const res = await api.get(`/customers?${params}`);
      setCustomers(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [page, search, activeFilter]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleOpenEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({
      full_name: customer.full_name || "", company_name: customer.company_name || "",
      company_address: customer.company_address || "", gst_number: customer.gst_number || "",
      contact_phone: customer.contact_phone || "",
      cutting_cost_matrix: customer.cutting_cost_matrix || []
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;
    setSubmitting(true);
    try {
      await api.put(`/customers/${editingCustomer.id}/cutting-cost`, {
        cutting_cost_matrix: form.cutting_cost_matrix,
      });
      toast.success("Customer cutting cost matrix updated");
      setDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update customer");
    } finally {
      setSubmitting(false);
    }
  };

  const addMatrixRow = () => {
    setForm(prev => ({
      ...prev,
      cutting_cost_matrix: [...(prev.cutting_cost_matrix || []), { thickness: "", moq: 1, rate: 0 }],
    }));
  };

  const removeMatrixRow = (idx) => {
    setForm(prev => ({
      ...prev,
      cutting_cost_matrix: (prev.cutting_cost_matrix || []).filter((_, i) => i !== idx),
    }));
  };

  const updateMatrixRow = (idx, field, value) => {
    setForm(prev => {
      const matrix = [...(prev.cutting_cost_matrix || [])];
      matrix[idx] = { ...matrix[idx], [field]: value };
      return { ...prev, cutting_cost_matrix: matrix };
    });
  };

  const toggleActive = async (customerId, currentStatus) => {
    if (!currentStatus) {
      // Activating — show password dialog
      setActivatingCustomer(customerId);
      setActivatePassword("");
      setShowPassword(false);
      setActivateDialogOpen(true);
      return;
    }
    // Deactivating — no password needed
    setTogglingActive(prev => ({ ...prev, [customerId]: true }));
    try {
      await api.put(`/customers/${customerId}/activate?is_active=false`);
      toast.success("Customer deactivated");
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setTogglingActive(prev => ({ ...prev, [customerId]: false }));
    }
  };

  const handleActivateWithPassword = async () => {
    if (!activatePassword || activatePassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setTogglingActive(prev => ({ ...prev, [activatingCustomer]: true }));
    try {
      await api.put(`/customers/${activatingCustomer}/activate?is_active=true&password=${encodeURIComponent(activatePassword)}`);
      toast.success("Customer activated with password set");
      setActivateDialogOpen(false);
      setActivatePassword("");
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate");
    } finally {
      setTogglingActive(prev => ({ ...prev, [activatingCustomer]: false }));
    }
  };

  const sendCredentials = async (customerId, customerEmail) => {
    setSendingEmail(prev => ({ ...prev, [customerId]: true }));
    try {
      await api.post(`/customers/${customerId}/send-credentials`);
      toast.success(`Credentials sent to ${customerEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(prev => ({ ...prev, [customerId]: false }));
    }
  };

  if (!isSuperAdmin()) {
    return <div className="text-center py-16"><p className="text-slate-500">Access restricted to Super Admin only</p></div>;
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase tracking-tight" data-testid="customers-title">Customers</h1>
          <p className="text-slate-500 mt-1">Manage customer accounts, activation, and credentials</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl rounded-none">
            <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider">Edit Customer — Cutting Cost Matrix</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wider">Customer Code</Label><Input value={editingCustomer?.customer_code || "N/A"} className="rounded-none bg-slate-100" disabled /></div>
                <div><Label className="text-xs uppercase tracking-wider">Email</Label><Input value={editingCustomer?.email || ""} className="rounded-none bg-slate-100" disabled /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wider">Full Name</Label><Input value={form.full_name} className="rounded-none bg-slate-100" disabled /></div>
                <div><Label className="text-xs uppercase tracking-wider">Company</Label><Input value={form.company_name} className="rounded-none bg-slate-100" disabled /></div>
              </div>

              {/* Cutting Cost Matrix */}
              <div className="border border-orange-200 bg-orange-50">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-orange-200">
                  <Scissors className="w-4 h-4 text-orange-600" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-orange-800">Cutting Cost Matrix</span>
                </div>
                <div className="p-3 space-y-3">
                  <p className="text-[11px] text-orange-700">
                    Define per-thickness and MOQ cutting rates for this customer. Leave thickness blank to match any thickness. Overrides the global matrix when set.
                  </p>

                  <div className="border border-orange-200">
                    <div className="grid grid-cols-[1fr_1fr_1fr_36px] gap-2 bg-orange-100 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-orange-700">
                      <span>Thickness (mm)</span>
                      <span>Min. Qty (MOQ)</span>
                      <span>Rate (₹/pcs)</span>
                      <span></span>
                    </div>

                    {(form.cutting_cost_matrix || []).length === 0 && (
                      <div className="px-3 py-4 text-center text-xs text-orange-600">
                        No entries — global matrix rates will apply.
                      </div>
                    )}

                    {(form.cutting_cost_matrix || []).map((row, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-[1fr_1fr_1fr_36px] gap-2 px-2 py-1.5 border-t border-orange-100 items-center"
                        data-testid={`customer-matrix-row-${idx}`}
                      >
                        <Input
                          type="number" step="0.01" min="0"
                          value={row.thickness ?? ""}
                          onChange={(e) => updateMatrixRow(idx, "thickness", e.target.value === "" ? null : parseFloat(e.target.value))}
                          className="rounded-none font-mono h-7 text-xs"
                          placeholder="Any"
                        />
                        <Input
                          type="number" step="1" min="1"
                          value={row.moq ?? 1}
                          onChange={(e) => updateMatrixRow(idx, "moq", parseInt(e.target.value) || 1)}
                          className="rounded-none font-mono h-7 text-xs"
                        />
                        <Input
                          type="number" step="0.0001" min="0"
                          value={row.rate ?? 0}
                          onChange={(e) => updateMatrixRow(idx, "rate", parseFloat(e.target.value) || 0)}
                          className="rounded-none font-mono h-7 text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => removeMatrixRow(idx)}
                          className="flex items-center justify-center w-7 h-7 text-orange-400 hover:text-red-600 hover:bg-red-50 border border-orange-200"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    onClick={addMatrixRow}
                    variant="outline"
                    className="h-8 rounded-none border-orange-300 text-orange-700 hover:bg-orange-100 text-xs"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Add Row
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-slate-900 text-white rounded-none" data-testid="save-customer-button">
                  {submitting ? "Saving..." : "Save Matrix"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Activate with Password Dialog */}
        <Dialog open={activateDialogOpen} onOpenChange={setActivateDialogOpen}>
          <DialogContent className="max-w-sm rounded-none">
            <DialogHeader><DialogTitle className="font-heading text-lg uppercase tracking-wider">Activate Customer</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-slate-600">Set a login password for this customer. This will be included in the activation email.</p>
              <div>
                <Label className="text-xs uppercase tracking-wider">Password *</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={activatePassword}
                    onChange={(e) => setActivatePassword(e.target.value)}
                    placeholder="Min 6 characters"
                    className="rounded-none font-mono pr-10"
                    data-testid="activate-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {activatePassword && activatePassword.length < 6 && (
                  <p className="text-xs text-red-500 mt-1">Password must be at least 6 characters</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setActivateDialogOpen(false)} className="rounded-none">Cancel</Button>
                <Button
                  onClick={handleActivateWithPassword}
                  disabled={!activatePassword || activatePassword.length < 6 || togglingActive[activatingCustomer]}
                  className="bg-green-700 hover:bg-green-800 text-white rounded-none"
                  data-testid="confirm-activate-button"
                >
                  {togglingActive[activatingCustomer] ? "Activating..." : "Activate & Set Password"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input placeholder="Search by name, code, company, email..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-9 rounded-none" data-testid="search-input" />
        </div>
        <div className="flex gap-1">
          {[["all","All"],["active","Active"],["inactive","Inactive"]].map(([v,l]) => (
            <button key={v} onClick={() => { setActiveFilter(v); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-none border transition-colors ${activeFilter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <Card className="card-industrial overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {[["customer_code","Code"],["full_name","Name"],["company_name","Company"],["email","Email"]].map(([f,l]) => (
                <TableHead key={f} className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleSort(f)}>
                  <span className="inline-flex items-center">{l}<SortIcon field={f} /></span>
                </TableHead>
              ))}
              <TableHead className="bg-slate-900 text-white">Phone</TableHead>
              <TableHead className="bg-slate-900 text-white">Cutting Matrix</TableHead>
              <TableHead className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleSort("is_active")}>
                <span className="inline-flex items-center">Status<SortIcon field="is_active" /></span>
              </TableHead>
              <TableHead className="bg-slate-900 text-white">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>
            ) : customers.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500"><Users className="w-12 h-12 mx-auto mb-3 opacity-20" /><p>No customers found</p></TableCell></TableRow>
            ) : (
              sortedCustomers.map((customer) => (
                <TableRow key={customer.id} className="hover:bg-slate-50" data-testid={`customer-row-${customer.id}`}>
                  <TableCell className="font-mono text-sm">{customer.customer_code || "—"}</TableCell>
                  <TableCell className="font-medium">{customer.full_name}</TableCell>
                  <TableCell><div><p>{customer.company_name}</p><p className="text-xs text-slate-500">{customer.gst_number}</p></div></TableCell>
                  <TableCell className="font-mono text-sm">{customer.email}</TableCell>
                  <TableCell>{customer.contact_phone}</TableCell>
                  <TableCell>
                    {(customer.cutting_cost_matrix || []).length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-mono bg-orange-50 border border-orange-200 text-orange-700 px-2 py-0.5">
                        <Scissors className="w-3 h-3" />
                        {customer.cutting_cost_matrix.length} {customer.cutting_cost_matrix.length === 1 ? "entry" : "entries"}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">— global</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={customer.is_active} onCheckedChange={() => toggleActive(customer.id, customer.is_active)} disabled={togglingActive[customer.id]} data-testid={`active-toggle-${customer.id}`} />
                      <Badge className={customer.is_active ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800 border-red-200"}>{customer.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(customer)} className="h-8 w-8 p-0" data-testid={`edit-customer-${customer.id}`}><Edit size={16} /></Button>
                      {customer.is_active && (
                        <Button size="sm" variant="outline" onClick={() => sendCredentials(customer.id, customer.email)} disabled={sendingEmail[customer.id]} className="h-8 gap-1 text-xs" data-testid={`send-mail-${customer.id}`}>
                          {sendingEmail[customer.id] ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send Mail
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="p-4">
          <Pagination page={page} totalPages={totalPages} total={total} perPage={perPage} onPageChange={setPage} />
        </div>
      </Card>
    </div>
  );
}
